import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { canonicalizePhoneBR } from '@/utils/phone-canonical';
import { resolverConexaoDoUsuario } from '@/lib/conexaoDoUsuario';
import { fetchPedidoContato } from '@/lib/fetchPedidoContato';

export interface AlvoConversa {
  familiaId: string;
  pedidoId: string;
  clienteId: string | null;
  clienteNome: string | null;
  documento: string | null;
  tipoPessoa: string | null;
  numeroPedido: string | null;
  fila: string | null;
  conversaId: string | null;
}

interface Props {
  alvo: AlvoConversa;
  open: boolean;
  onClose: () => void;
  /** chamado com a conversa recém-criada, para a gaveta abrir em seguida */
  onVinculada: (conversaId: string) => void;
}

const soDigitos = (v: string | null | undefined) => (v || '').replace(/\D/g, '');

/* 22% das famílias do Kanban (1.592 de 7.122, medido 17/09/2026) não têm
   conversa. O card não pode abrir uma gaveta vazia nem mandar a pessoa para o
   Inbox procurar: aqui ela confirma o número e a conversa nasce vinculada.
   1.074 desses já têm telefone no CRM — por isso o campo abre preenchido. */
export const VincularConversaDialog: React.FC<Props> = ({ alvo, open, onClose, onVinculada }) => {
  const [telefone, setTelefone] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [criando, setCriando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTelefone('');
    setBuscando(true);
    fetchPedidoContato(alvo.pedidoId)
      .then((c) => setTelefone(c?.telefone || ''))
      .catch(() => setTelefone(''))
      .finally(() => setBuscando(false));
  }, [open, alvo.pedidoId]);

  const criar = async () => {
    const canonico = canonicalizePhoneBR(telefone);
    if (canonico.length < 12) {
      toast.error('Número incompleto. Use DDD + número, como 51 99999-0000.');
      return;
    }
    setCriando(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth.user?.id;
      if (!userId) { toast.error('Sessão expirada. Faça login novamente.'); return; }

      const connectionId = await resolverConexaoDoUsuario(userId);
      if (!connectionId) {
        toast.error('Você não tem uma conexão WhatsApp configurada. Configure em Meu Perfil → Minha Conexão.');
        return;
      }

      /* ⛔ owner VAZIO de propósito (Lei do Dono no Inbox, 09/09/2026). Quem
         clica no card do Kanban é muitas vezes o BKO: gravá-lo em
         `owner_user_id` o tornaria dono da conversa E calaria a IsabellA por
         30 min. O dono do CLIENTE mora em `consultor_comercial_id`, e quem o
         carimba é o gatilho `trg_wconv_dono_do_cnpj` quando o CNPJ é vinculado
         logo abaixo. */
      const { data: convId, error } = await supabase.rpc('whatsapp_find_or_create_conversation', {
        p_telefone: canonico,
        p_connection_id: connectionId,
      });
      if (error || !convId) throw error || new Error('conversation_id vazio');

      const { data: ped } = await supabase
        .from('pedidos')
        .select('lead_origem_id')
        .eq('id', alvo.pedidoId)
        .maybeSingle();

      const vinculo: { lead_id?: string; cliente_cnpj?: string } = {};
      const leadId = (ped as { lead_origem_id?: string | null } | null)?.lead_origem_id;
      if (leadId) vinculo.lead_id = leadId;
      /* ⚠️ `cliente_cnpj` guarda CNPJ e só CNPJ — em 29.922 conversas não há um
         CPF sequer, e forçar um ali é o mesmo erro que já fabricou CNPJ falso a
         partir de CPF. Cliente PF se vincula pelo lead. */
      const doc = soDigitos(alvo.documento);
      if (doc.length === 14) vinculo.cliente_cnpj = doc;

      if (Object.keys(vinculo).length > 0) {
        const { error: erroVinculo } = await supabase
          .from('whatsapp_conversations')
          .update(vinculo)
          .eq('id', convId);
        if (erroVinculo) throw erroVinculo;
      }

      toast.success('Conversa criada e vinculada ao pedido.');
      onVinculada(convId);
    } catch (e) {
      console.error('[VincularConversaDialog] falha ao criar conversa', e);
      toast.error('Não foi possível criar a conversa. Confira o número e tente de novo.');
    } finally {
      setCriando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-base">Este pedido ainda não tem conversa</DialogTitle>
          <DialogDescription className="text-xs">
            Confirme o WhatsApp do cliente. A conversa nasce vinculada ao pedido e abre aqui mesmo.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-border-warm bg-muted/40 px-3 py-2">
          <div className="truncate text-[13px] font-semibold text-foreground">
            {alvo.clienteNome || 'Cliente sem nome'}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
            {alvo.documento || 'sem documento'}
            {alvo.numeroPedido ? ` · ${alvo.numeroPedido}` : ''}
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="vincular-telefone" className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            WhatsApp do cliente
          </label>
          <div className="relative">
            <Input
              id="vincular-telefone"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !criando) void criar(); }}
              placeholder="51 99999-0000"
              disabled={buscando}
              autoFocus
            />
            {buscando && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>
          <p className="text-[10px] text-muted-foreground">
            {buscando
              ? 'Procurando o telefone do cliente no CRM…'
              : telefone
                ? 'Veio do cadastro do cliente. Corrija se não for esse.'
                : 'Não achamos telefone deste cliente no CRM — digite o número.'}
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} disabled={criando} className="h-8 text-xs">
            Cancelar
          </Button>
          <Button onClick={() => void criar()} disabled={criando || buscando} className="h-8 text-xs">
            {criando ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Criar conversa
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
